// src/utils/computeWarning.ts
import type { Recipe, RecipeIngredient } from '../types/recipe';
import type { Prefs } from '../types/preferences';

export type WarningType = 'avoid' | 'allergy' | 'mixed';

export interface WarningReason {
  name: string;
  category?: string;
  type: 'ingredient' | 'tag';
  warningType: 'allergy' | 'avoid';
}

export function computeWarning(
  recipe: Recipe,
  prefs: Prefs
): { type: WarningType; reasons: WarningReason[] } | null {
  const ingredientNames = recipe.ingredients
    .map(
      (ing: RecipeIngredient) => ing.catalog?.nameCanonical || ing.name || ''
    )
    .filter(Boolean);

  // Allergens
  const allergyReasons: WarningReason[] = ingredientNames
    .filter((n) => prefs.allergic.includes(n))
    .map((name) => ({
      name,
      type: 'ingredient' as const,
      warningType: 'allergy' as const,
    }));

  // Avoided ingredients
  const avoidIngReasons: WarningReason[] = ingredientNames
    .filter((n) => prefs.avoided.includes(n))
    .map((name) => ({
      name,
      type: 'ingredient' as const,
      warningType: 'avoid' as const,
    }));

  // Avoided tags
  const avoidTagReasons: WarningReason[] = (recipe.recipeTags || [])
    .filter((rt) => prefs.excludedTagIds.includes(rt.tag.id))
    .map((rt) => ({
      name: rt.tag.name,
      category: rt.tag.category.replace('_', ' ').toLowerCase(),
      type: 'tag' as const,
      warningType: 'avoid' as const,
    }));

  const allReasons = [
    ...allergyReasons,
    ...avoidIngReasons,
    ...avoidTagReasons,
  ];

  // De-duplicate by name + type + warningType
  const uniq = Array.from(
    new Map(
      allReasons.map((r) => [`${r.name}-${r.type}-${r.warningType}`, r])
    ).values()
  );

  if (!uniq.length) return null;

  const hasAllergy = uniq.some((r) => r.warningType === 'allergy');
  const hasAvoid = uniq.some((r) => r.warningType === 'avoid');

  let type: WarningType = 'avoid';
  if (hasAllergy && hasAvoid) type = 'mixed';
  else if (hasAllergy) type = 'allergy';

  return { type, reasons: uniq };
}
